import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";

/**
 * Opens an authenticated PDF.
 *
 * These used to be plain <a href> links. A browser navigation carries no
 * Authorization header, so every one of them came back 401 once the API
 * required a token. Fetching through HttpClient lets the auth interceptor
 * attach it, and the result is shown from an object URL.
 */
@Injectable({ providedIn: "root" })
export class PdfDownloadService {
  private base = environment.baseUrl;
  busy = false;

  constructor(private http: HttpClient) {}

  /** `path` is relative to the API root, e.g. "Idcard/downloadIcard/2025-2026/0/113/24/164". */
  open(path: string, filename?: string, keepType = false): void {
    this.busy = true;

    // The tab is opened NOW, inside the click, because a window.open() issued
    // after the HTTP response is no longer part of a user gesture and Chrome
    // blocks it silently - the icon just appeared to do nothing. The blank tab
    // is pointed at the blob once it arrives.
    const tab = filename ? null : window.open("", "_blank");

    this.http.get(this.base + path, { responseType: "blob" }).subscribe(
      (blob: Blob) => {
        this.busy = false;
        // Some browsers hand back a generic type; a PDF must be typed as one or
        // the tab offers a download instead of rendering it.
        const typed =
          blob.type === "application/pdf" || (keepType && blob.type.startsWith("image/"))
            ? blob
            : new Blob([blob], { type: keepType ? this.typeOf(path) : "application/pdf" });
        const url = URL.createObjectURL(typed);
        if (filename) {
          this.saveAs(url, filename);
        } else if (tab && !tab.closed) {
          tab.location.href = url;
        } else {
          // Popup blocked outright: a download is not blocked, so fall back to it
          // rather than losing the file.
          this.saveAs(url, path.split("/").pop() + ".pdf");
        }
        // Revoked late: the new tab has to finish reading it first.
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      (err) => {
        this.busy = false;
        if (tab && !tab.closed) tab.close();
        // responseType 'blob' applies to the error body too, so the server's
        // `{ "error": "..." }` arrives as a Blob and has to be read back out.
        // Those messages are the useful part - "no students marked present",
        // "certificates not released yet" - and a flat "try again" hides them.
        this.readError(err).then((msg) => window.alert(msg));
      },
    );
  }

  /** Opens an uploaded file (PDF or image) in a new tab; `path` like "upload/sgfi/x.jpg". */
  openFile(path: string): void {
    this.open(path, undefined, true);
  }

  /** Raw authenticated fetch of an uploaded file. */
  fetchBlob(path: string): Observable<Blob> {
    return this.http.get(this.base + path, { responseType: "blob" });
  }

  /** Object URL for an authenticated image, for <img src>. Revoke when done. */
  imageUrl(path: string): Observable<string> {
    return this.http
      .get(this.base + path, { responseType: "blob" })
      .pipe(map((blob: Blob) => URL.createObjectURL(blob)));
  }

  private typeOf(path: string): string {
    const ext = (path.split(".").pop() || "").toLowerCase();
    if (ext === "png") return "image/png";
    if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
    return "application/pdf";
  }

  private saveAs(url: string, filename: string): void {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  private readError(err: any): Promise<string> {
    const fallback = "Could not open that file. Please try again.";
    const body = err && err.error;
    if (!(body instanceof Blob)) {
      return Promise.resolve((body && body.error) || fallback);
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(JSON.parse(String(reader.result)).error || fallback);
        } catch (e) {
          resolve(fallback);
        }
      };
      reader.onerror = () => resolve(fallback);
      reader.readAsText(body);
    });
  }
}
